import { pickT } from "@repo/shared";
import {
  PDF_DEFAULT_TABLE_COLUMN_WIDTH,
  PDF_TABLE_COLUMN_WIDTHS,
  type PdfSectionScope,
  type PdfTableColumnWidth,
} from "@repo/shared/types";
import { Trash2 } from "lucide-react";
import { useMemo } from "react";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuGroup,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import { useUserLanguage } from "@/lib/i18n/context";
import { useLocalize } from "@/lib/i18n/use-localize";
import { useT } from "@/lib/i18n/use-t";
import {
  buildFieldMap,
  flowFieldDisplayRef,
  transformToDisplay,
  transformToStorage,
} from "@/lib/services/placeholder-transformer";
import { AdvisorNoteMenuItems } from "./advisor-note-menu-items";
import { T } from "./table-form.i18n";
import { type ComponentFormProps, NO_ADVISOR_NOTE_PAGES } from "./types";

export type TableRow = {
  id: string;
  col1: string;
  col2: string;
};

export type TableConfig = {
  title?: string;
  description?: string;
  rows: TableRow[];
  // QL-384 — all optional; absent means today's rendering.
  headerRow?: boolean;
  headerColumn?: boolean;
  columnWidth?: PdfTableColumnWidth;
};

type TableFormProps = ComponentFormProps<TableConfig> & {
  sectionScope?: PdfSectionScope;
};

export function TableForm({
  config,
  onChange,
  flowFields,
  // QL-13 — `{{Quiz.<name>}}` renders the client's result for that quiz.
  quizzes: activeQuizzes = [],
  advisorNotePages = NO_ADVISOR_NOTE_PAGES,
  clientCustomFields = [],
  sectionScope,
}: TableFormProps) {
  const { userLanguage } = useUserLanguage();
  const localize = useLocalize();
  const t = useT(T);
  const rows = config.rows || getDefaultTableConfig().rows;

  // Build field map for placeholder transformation
  const fieldMap = useMemo(
    () =>
      buildFieldMap(clientCustomFields, flowFields, userLanguage, activeQuizzes, advisorNotePages),
    [clientCustomFields, flowFields, userLanguage, activeQuizzes, advisorNotePages],
  );

  // Transform rows from storage (IDs) to display (names) format
  const displayRows = useMemo(
    () =>
      rows.map((row) => ({
        ...row,
        col1: transformToDisplay(row.col1, fieldMap),
        col2: transformToDisplay(row.col2, fieldMap),
      })),
    [rows, fieldMap],
  );

  // Transform title/description from storage to display
  const displayTitle = useMemo(
    () => transformToDisplay(config.title || "", fieldMap),
    [config.title, fieldMap],
  );
  const displayDescription = useMemo(
    () => transformToDisplay(config.description || "", fieldMap),
    [config.description, fieldMap],
  );

  const updateRow = (id: string, field: "col1" | "col2", displayValue: string) => {
    // Transform display value back to storage format
    const storageValue = transformToStorage(displayValue, fieldMap);
    onChange({
      ...config,
      rows: rows.map((r) => (r.id === id ? { ...r, [field]: storageValue } : r)),
    });
  };

  const handleTitleChange = (displayValue: string) => {
    const storageValue = transformToStorage(displayValue, fieldMap);
    onChange({ ...config, title: storageValue });
  };

  const handleDescriptionChange = (displayValue: string) => {
    const storageValue = transformToStorage(displayValue, fieldMap);
    onChange({ ...config, description: storageValue });
  };

  // When adding row, use display name (will be transformed on subsequent edits)
  const addRow = (label?: string, displayPlaceholder?: string) => {
    // Transform the placeholder to storage format (ID-based) before saving
    const storagePlaceholder = displayPlaceholder
      ? transformToStorage(`{{${displayPlaceholder}}}`, fieldMap)
      : "";
    onChange({
      ...config,
      rows: [
        ...rows,
        {
          id: crypto.randomUUID(),
          col1: label || "",
          col2: storagePlaceholder,
        },
      ],
    });
  };

  const removeRow = (id: string) => {
    onChange({
      ...config,
      rows: rows.filter((r) => r.id !== id),
    });
  };

  // Client fields (always available)
  const clientFields = [
    { label: t.clientName, value: "Client.Name" },
    { label: t.clientType, value: "Client.Type" },
    { label: t.clientEmail, value: "Client.Email" },
    { label: t.clientMobilePhone, value: "Client.MobilePhone" },
    { label: t.clientSSN, value: "Client.SSN" },
    { label: t.clientOrgId, value: "Client.OrgId" },
    { label: t.clientCountry, value: "Client.Country" },
    { label: t.clientGroupMembers, value: "Client.Members" },
    { label: t.clientRepresentative, value: "Client.Representative" },
  ];

  // Owner fields (advisor / client owner data)
  const ownerFields = [
    { label: t.ownerName, value: "Owner.Name" },
    { label: t.ownerEmail, value: "Owner.Email" },
    { label: t.ownerPhone, value: "Owner.Phone" },
    { label: t.ownerTitle, value: "Owner.Title" },
    { label: t.ownerSignature, value: "Owner.Signature" },
  ];

  // Flow fields (flow instance data)
  const flowInstanceFields = [
    { label: t.flowName, value: "Flow.Name" },
    { label: t.cancelReason, value: "Flow.CancelReason" },
    { label: t.expectingChanges, value: "Flow.ExpectingFinancialChanges" },
    { label: t.expectingChangesNote, value: "Flow.ExpectingFinancialChangesNote" },
  ];

  // Document fields (always available)
  const documentFields = [{ label: t.today, value: "today" }];

  // Goal fields (only available in per_goal scope)
  const goalFields = [
    { label: t.goalName, value: "Goal Name" },
    { label: t.goalType, value: "Goal Type" },
    { label: t.goalDescription, value: "Goal Description" },
    { label: t.riskScore, value: "Risk Score" },
    // QL-375 group 10 — the goal's mandate (empty on a goal without one)
    { label: t.mandateName, value: "Mandate Name" },
    { label: t.mandateStatus, value: "Mandate Status" },
    { label: t.mandateSource, value: "Mandate Source" },
    { label: t.mandateOrigin, value: "Mandate Origin" },
    { label: t.mandateMode, value: "Mandate Mode" },
    { label: t.existingMandateName, value: "Existing Mandate Name" },
    { label: t.mandateDeviationNote, value: "Mandate Deviation Note" },
    { label: t.mandateDecidedBy, value: "Mandate Decided By" },
    { label: t.mandateDecidedAt, value: "Mandate Decided At" },
  ];
  // Per-portfolio sections only: QL-375 group 10b mandate verdict + QL-72 model deviation
  const portfolioSectionFields = [
    { label: t.portfolioMandateStatus, value: "Portfolio Mandate Status" },
    { label: t.portfolioMandateDeviationNote, value: "Portfolio Mandate Deviation Note" },
    { label: t.portfolioMandateDecidedBy, value: "Portfolio Mandate Decided By" },
    { label: t.portfolioMandateDecidedAt, value: "Portfolio Mandate Decided At" },
    // QL-72 — model portfolio picked from another risk score
    { label: t.portfolioModelDeviation, value: "Portfolio Model Deviation" },
    { label: t.portfolioModelRiskScore, value: "Portfolio Model Risk Score" },
    { label: t.portfolioGoalRiskScore, value: "Portfolio Goal Risk Score" },
  ];
  // QL-414 — the portfolio's own name, where a section prints per portfolio.
  if (sectionScope === "per_portfolio") {
    goalFields.push({ label: t.portfolioName, value: "Portfolio Name" });
  }

  // QP-150: per_portfolio sections also carry the parent goal's context, so goal fields apply.
  const isPerGoalScope = sectionScope === "per_goal" || sectionScope === "per_portfolio";

  return (
    <div className="space-y-4">
      <div className="space-y-2">
        <Label htmlFor="table-title">{t.title}</Label>
        <Input
          id="table-title"
          value={displayTitle}
          onChange={(e) => handleTitleChange(e.target.value)}
          placeholder={t.titlePlaceholder}
        />
        <p className="text-xs text-muted-foreground">{t.titleHelp}</p>
      </div>

      <div className="space-y-2">
        <Label htmlFor="table-description">{t.description}</Label>
        <Textarea
          id="table-description"
          value={displayDescription}
          onChange={(e) => handleDescriptionChange(e.target.value)}
          placeholder={t.descriptionPlaceholder}
          rows={2}
        />
        <p className="text-xs text-muted-foreground">{t.descriptionHelp}</p>
      </div>

      <div className="space-y-2">
        <Label>
          {t.tableRows} <span className="text-destructive">*</span>
        </Label>
        <p className="text-xs text-muted-foreground mb-3">{t.tableRowsHelp}</p>

        <div className="space-y-2">
          {displayRows.map((row) => (
            <div key={row.id} className="flex gap-2 items-center">
              <Input
                value={row.col1}
                onChange={(e) => updateRow(row.id, "col1", e.target.value)}
                placeholder={t.labelPlaceholder}
                className="flex-1"
              />
              <Input
                value={row.col2}
                onChange={(e) => updateRow(row.id, "col2", e.target.value)}
                placeholder={t.valuePlaceholder}
                className="flex-1"
              />
              {displayRows.length > 1 && (
                <Button
                  type="button"
                  variant="ghost"
                  size="icon"
                  tooltip={t.removeRow}
                  onClick={() => removeRow(row.id)}
                >
                  <Trash2 className="h-4 w-4" />
                </Button>
              )}
            </div>
          ))}
        </div>

        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button type="button" variant="outline" size="sm" className="mt-2">
              {t.addRow}
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="start" className="w-56 max-h-[400px] overflow-y-auto">
            <DropdownMenuItem onClick={() => addRow()}>
              <span className="font-medium">{t.emptyRow}</span>
            </DropdownMenuItem>

            <DropdownMenuSeparator />
            <DropdownMenuLabel>{t.clientFields}</DropdownMenuLabel>
            <DropdownMenuGroup>
              {clientFields.map((field) => (
                <DropdownMenuItem
                  key={field.value}
                  onClick={() => addRow(field.label, field.value)}
                >
                  {field.label}
                </DropdownMenuItem>
              ))}
            </DropdownMenuGroup>

            {(() => {
              const customOnly = clientCustomFields.filter((f) => f.isActive && !f.isStandard);
              return customOnly.length > 0 ? (
                <>
                  <DropdownMenuSeparator />
                  <DropdownMenuLabel>{t.customFields}</DropdownMenuLabel>
                  <DropdownMenuGroup>
                    {customOnly.map((field) => {
                      // Insert display name - it will be converted to ID on save
                      const displayName = localize(field.nameI18n) || field.name;
                      return (
                        <DropdownMenuItem
                          key={field.id}
                          onClick={() => addRow(displayName, `Client.${displayName}`)}
                        >
                          {displayName}
                        </DropdownMenuItem>
                      );
                    })}
                  </DropdownMenuGroup>
                </>
              ) : null;
            })()}

            <DropdownMenuSeparator />
            <DropdownMenuLabel>{t.ownerFields}</DropdownMenuLabel>
            <DropdownMenuGroup>
              {ownerFields.map((field) => (
                <DropdownMenuItem
                  key={field.value}
                  onClick={() => addRow(field.label, field.value)}
                >
                  {field.label}
                </DropdownMenuItem>
              ))}
            </DropdownMenuGroup>

            {isPerGoalScope && (
              <>
                <DropdownMenuSeparator />
                <DropdownMenuLabel>{t.goalFields}</DropdownMenuLabel>
                <DropdownMenuGroup>
                  {goalFields.map((field) => (
                    <DropdownMenuItem
                      key={field.value}
                      onClick={() => addRow(field.label, field.value)}
                    >
                      {field.label}
                    </DropdownMenuItem>
                  ))}
                </DropdownMenuGroup>
              </>
            )}

            {sectionScope === "per_portfolio" && (
              <>
                <DropdownMenuSeparator />
                <DropdownMenuLabel>{t.portfolioFields}</DropdownMenuLabel>
                <DropdownMenuGroup>
                  {portfolioSectionFields.map((field) => (
                    <DropdownMenuItem
                      key={field.value}
                      onClick={() => addRow(field.label, field.value)}
                    >
                      {field.label}
                    </DropdownMenuItem>
                  ))}
                </DropdownMenuGroup>
              </>
            )}

            {(() => {
              // Filter flow fields by scope:
              // - In global sections: only show global-scoped fields
              // - In per_goal sections: show all fields
              const filteredFlowFields = flowFields.filter((field) => {
                if (field.scope === "portfolio" && sectionScope !== "per_portfolio") return false;
                if (field.scope === "goal" && sectionScope === "global") return false;
                return true;
              });

              return filteredFlowFields.length > 0 ? (
                <>
                  <DropdownMenuSeparator />
                  <DropdownMenuLabel>{t.flowFields}</DropdownMenuLabel>
                  <DropdownMenuGroup>
                    {filteredFlowFields.map((field) => {
                      // Insert display name - it will be converted to ID on save
                      // (by id when another field shares the name, QL-610)
                      const displayName = field.name;
                      return (
                        <DropdownMenuItem
                          key={field.id}
                          onClick={() => addRow(displayName, flowFieldDisplayRef(field, fieldMap))}
                        >
                          {displayName}
                          {field.scope === "goal" && (
                            <span className="ml-2 text-xs text-muted-foreground">
                              {t.goalScope}
                            </span>
                          )}
                          {field.scope === "portfolio" && (
                            <span className="ml-2 text-xs text-muted-foreground">
                              {t.portfolioScope}
                            </span>
                          )}
                        </DropdownMenuItem>
                      );
                    })}
                  </DropdownMenuGroup>
                </>
              ) : null;
            })()}
            <DropdownMenuSeparator />
            <DropdownMenuLabel>{t.flow}</DropdownMenuLabel>
            <DropdownMenuGroup>
              {flowInstanceFields.map((field) => (
                <DropdownMenuItem
                  key={field.value}
                  onClick={() => addRow(field.label, field.value)}
                >
                  {field.label}
                </DropdownMenuItem>
              ))}
            </DropdownMenuGroup>
            <AdvisorNoteMenuItems
              pages={advisorNotePages}
              sectionScope={sectionScope}
              onInsert={(label, field) => addRow(label, field)}
            />
            {activeQuizzes.length > 0 && (
              <>
                <DropdownMenuSeparator />
                <DropdownMenuLabel>{t.quizFields}</DropdownMenuLabel>
                <DropdownMenuGroup>
                  {activeQuizzes.map((quiz) => (
                    <DropdownMenuItem
                      key={quiz.id}
                      onClick={() => addRow(quiz.name, `Quiz.${quiz.name}`)}
                    >
                      {quiz.name}
                    </DropdownMenuItem>
                  ))}
                </DropdownMenuGroup>
                <DropdownMenuLabel>{t.quizOverrideFields}</DropdownMenuLabel>
                <DropdownMenuGroup>
                  {activeQuizzes.map((quiz) => (
                    <DropdownMenuItem
                      key={quiz.id}
                      onClick={() => addRow(quiz.name, `Quiz.${quiz.name}.Override`)}
                    >
                      {quiz.name}
                    </DropdownMenuItem>
                  ))}
                </DropdownMenuGroup>
                <DropdownMenuLabel>{t.quizExternalFields}</DropdownMenuLabel>
                <DropdownMenuGroup>
                  {activeQuizzes.map((quiz) => (
                    <DropdownMenuItem
                      key={quiz.id}
                      onClick={() => addRow(quiz.name, `Quiz.${quiz.name}.External`)}
                    >
                      {quiz.name}
                    </DropdownMenuItem>
                  ))}
                </DropdownMenuGroup>
              </>
            )}
            <DropdownMenuSeparator />
            <DropdownMenuLabel>{t.document}</DropdownMenuLabel>
            <DropdownMenuGroup>
              {documentFields.map((field) => (
                <DropdownMenuItem
                  key={field.value}
                  onClick={() => addRow(field.label, field.value)}
                >
                  {field.label}
                </DropdownMenuItem>
              ))}
            </DropdownMenuGroup>
          </DropdownMenuContent>
        </DropdownMenu>
      </div>

      <div className="space-y-4 border-t pt-4">
        <div className="space-y-2">
          <Label htmlFor="table-column-width">{t.columnWidth}</Label>
          <Select
            value={config.columnWidth ?? PDF_DEFAULT_TABLE_COLUMN_WIDTH}
            onValueChange={(value) =>
              onChange({ ...config, columnWidth: value as PdfTableColumnWidth })
            }
          >
            <SelectTrigger id="table-column-width" className="w-48">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {PDF_TABLE_COLUMN_WIDTHS.map((width) => (
                <SelectItem key={width} value={width}>
                  {width.replace("/", " / ")}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        <div className="space-y-1">
          <div className="flex items-center space-x-2">
            <Checkbox
              id="table-header-row"
              checked={config.headerRow === true}
              onCheckedChange={(checked) => onChange({ ...config, headerRow: checked === true })}
            />
            <Label htmlFor="table-header-row" className="font-normal cursor-pointer">
              {t.headerRow}
            </Label>
          </div>
          <p className="text-xs text-muted-foreground pl-6">{t.headerRowHelp}</p>
        </div>

        <div className="flex items-center space-x-2">
          <Checkbox
            id="table-header-column"
            checked={config.headerColumn === true}
            onCheckedChange={(checked) => onChange({ ...config, headerColumn: checked === true })}
          />
          <Label htmlFor="table-header-column" className="font-normal cursor-pointer">
            {t.headerColumn}
          </Label>
        </div>
      </div>
    </div>
  );
}

export function validateTableConfig(config: TableConfig, lang = "en_GB"): string | null {
  const t = pickT(T, lang);
  const rows = config.rows || [];
  if (rows.length === 0) {
    return t.validationAtLeastOneRow;
  }

  const hasContent = rows.some((row) => row.col1.trim() || row.col2.trim());
  if (!hasContent) {
    return t.validationAtLeastOneCell;
  }

  return null;
}

export function getDefaultTableConfig(): TableConfig {
  return {
    rows: [{ id: crypto.randomUUID(), col1: "Name", col2: "{{Client.Name}}" }],
  };
}
